import React, { useState } from 'react';
import { Checkbox, Flex, Image, message, Modal } from 'antd';
import { CustomButton, CustomParagraph, CustomTitle } from '../../components';
import phoneAi from '../../assets/images/Aiphone.svg';
import { useToken } from '../../common/helper';
import { useSpin } from '../../components/SpinContext';
import callAi from '../../assets/images/callAi.svg';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import { dontShowMessage, DontShowMessageResponse } from '../../apis/shipment';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { lowerLabel } from '@/common/utils/utils';

interface CallAiAssistantModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: (dontShowAgain: boolean) => void;
}

const CallAiAssistantModal: React.FC<CallAiAssistantModalProps> = ({
  open,
  onCancel,
  onConfirm,
}) => {
  const { token, decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();
  const { showSpin, hideSpin } = useSpin();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    const isChecked = e.target.checked;
    setDontShowAgain(isChecked);
    const refreshToken = localStorage.getItem('@refreshToken');
    const payload = {
      ai_static_message: !isChecked,
      refresh: refreshToken,
    };

    showSpin();

    dontShowMessage(token, payload)
      .then((res: DontShowMessageResponse) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            message.success(data?.data?.message);
            if (data?.data?.data?.access) {
              localStorage.setItem('@token', data.data.data.access);
            }
            if (data?.data?.data?.refresh) {
              localStorage.setItem('@refreshToken', data.data.data.refresh);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        console.error('AI Call API Error:', error);
      })
      .finally(() => {
        hideSpin();
      });
  };
  return (
    <Modal
      className='ai-modal'
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={600}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img
            src={phoneAi}
            alt='AI Icon'
            style={{ width: '20px', height: '20px' }}
          />
          <CustomTitle
            text='Call using AI Assistant'
            fontSize='20px'
            lineHeight='28px'
            fontWeight='700'
            marginBottom='0px'
          />
        </div>
      }
    >
      <Image
        src={callAi}
        alt='AI call preview'
        preview={false}
        style={{
          width: '100%',
          borderRadius: '8px',
          marginBottom: '20px',
          marginTop: '10px',
        }}
      />
      <CustomParagraph
        text={`The Zoomlogi AI Assistant will call the party in question on ${
          decodedToken?.company_name
        }'s behalf to resolve the issue. You can review the call transcript at any time in the shipment’s live ${lowerLabel(
          themeT('chainOfCustody', theme.themeType)
        )} after the call is completed.`}
        fontSize='12px'
        fontWeight='400'
        lineHeight='20px'
        marginBottom='16px'
      />

      <Flex
        justify='space-between'
        align='center'
        style={{ marginTop: '32px' }}
      >
        <Checkbox
          onChange={handleCheckboxChange}
          style={{ fontWeight: 600, fontSize: '12px' }}
        >
          Don’t show this message again
        </Checkbox>

        <Flex gap='16px'>
          <CustomButton
            onClick={onCancel}
            text='Cancel'
            bgColor='transparent'
            color='black'
            fontSize='14px'
            fontWeight='600'
            boxShadow='none'
            height='48px'
          />

          <CustomButton
            onClick={() => {
              onConfirm(dontShowAgain);
            }}
            text='Call with AI Assistant'
            bgColor='black'
            color='white'
            height='48px'
            fontSize='14px'
            fontWeight='600'
            boxShadow='none'
            borderRadius='8px'
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

export default CallAiAssistantModal;
